import type {
    BrandCardCollectionElement,
    BrandCardCollectionEntry,
    CompanyFocusTopicContentSectionEntry,
    CompanyFocusTopicElement,
    CompanyFocusTopicElementEntry,
    CompanyFocusTopicExternalLinkEntry,
    CompanyFocusTopicInternalLinkEntry,
    ContentTree,
    ExternalLinkElement,
    ExternalLinkElementOptional,
    GovernanceRoleElement,
    GovernanceRolePersonProfile,
    HighlightsCollectionElement,
    HighlightsCollectionEntry,
    ImageElement,
    ImageElementOptional,
    ImageLinkCollectionElement,
    ImageLinkCollectionEntry,
    ImageLinkCollectionEntryExternal,
    ImageLinkCollectionEntryInternal,
    InfoBannerCollectionElement,
    InfoBannerCollectionEntry,
    InternalLinkElement,
    InternalLinkElementOptional,
    InternalLinkSectionElement,
    InternalLinkSectionEntryInternal,
    InternalLinkSectionEntryResourceLibrary,
    LinkCollectionElement,
    LinkCollectionEntryExternal,
    LinkCollectionEntryInternal,
    PartitionedTableElement,
    PartitionedTableEntry,
    PlainMultilineTextElement,
    PlainMultilineTextElementOptional,
    PlainTextElement,
    PlainTextElementOptional,
    ProductApplicationSegmentCardCollectionElement,
    ProductApplicationSegmentCardCollectionEntry,
    RichTextElement,
    RichTextElementOptional,
    SiteContentElement,
    TableElement,
} from '../types/SiteContentElement';
import {
    BrandCard,
    CompanyFocusTopicElementEntrySchema,
    ExternalLinkElementOptionalSchema,
    GetSiteContentElementListingSchema,
    GovernanceRolePersonProfileSchema,
    HighlightsCollectionEntrySchema,
    ImageElementOptionalSchema,
    ImageLinkCollectionEntrySchema,
    InfoBannerCollectionEntrySchema,
    InternalLinkElementOptionalSchema,
    InternalLinkSectionEntrySchema,
    LinkCollectionEntrySchema,
    PartitionedTableEntrySchema,
    PlainMultilineTextElementOptionalSchema,
    ProductApplicationSegmentCard,
} from '../schemas/SiteContentElementZodSchemas';
import { getAssetUrl } from '../../../../../utils/Pimcore';
import { sanitizeRichText } from './sanitizeRichText';
import { z } from 'zod';
import sanitizeRoute from '../../../common/tools/sanitizeRoute';

/**
 * Safely parse a stringified array (with single-quotes) into a JS array.
 * Falls back to an empty array if parsing fails or the input isn't a string.
 */
export function parseJsonArray<T>(input: unknown): T {
    if (typeof input === 'string') {
        try {
            // replace single-quotes with double-quotes so JSON.parse can handle it
            return JSON.parse(input.replace(/'/g, '"')) as T;
        } catch (err) {
            console.warn('parseJsonArray failed:', err);
            return [] as unknown as T;
        }
    }
    // if it's already an array, just cast it
    return (Array.isArray(input) ? input : []) as T;
}

/*
// If you ever need full JavaScript literal support (e.g. comments, trailing commas),
// you can switch to this—but be careful with untrusted input!
export function parseLiteralArray<T>(s: string): T {
  return new Function(`return ${s}`)() as T;
}
*/

function extractTableHeader(value: any): string[];
function extractTableHeader(value: any, returnUndefined: true): string[] | undefined;

/**
 * Get table header provided in Pimcore table field value.
 * Table header field is itself a Pimcore table, only with a single row, so we need to get the
 * first row.
 */
function extractTableHeader(
    value: any, // Value of Pimcore table field representing the table header
    returnUndefined = false
): string[] | undefined {
    try {
        const raw = parseJsonArray<string[]>(value);
        if (!Array.isArray(raw)) {
            return returnUndefined ? undefined : [];
        }
        const first = raw[0];
        if (!Array.isArray(first)) {
            return returnUndefined ? undefined : [];
        }
        return first;
    } catch {
        return returnUndefined ? undefined : [];
    }
}

function extractTableBody(value: any): string[][];
function extractTableBody(value: any, returnUndefined: true): string[][] | undefined;

/**
 * Get table body provided in Pimcore table field value.
 */
function extractTableBody(
    value: any, // Value of Pimcore table field representing the table body
    returnUndefined = false
): string[][] | undefined {
    try {
        const firstParse = typeof value === 'string' ? JSON.parse(value) : value;
        const raw = typeof firstParse === 'string' ? JSON.parse(firstParse) : firstParse;

        if (!Array.isArray(raw)) {
            return returnUndefined ? undefined : [];
        }

        return raw;
    } catch (error) {
        console.warn('[extractTableBody] Failed to parse tableBody:', error, value);
        return returnUndefined ? undefined : [];
    }
}

function hydrateExternalLinkElementOptional(
    node: z.infer<typeof ExternalLinkElementOptionalSchema>
): ExternalLinkElementOptional {
    return {
        kind: 'externalLinkOptional',
        key: node.key,
        linkText: node.linkText,
        linkUrl: node.linkUrl,
    };
}

function hydrateInternalLinkElementOptional(
    node: z.infer<typeof InternalLinkElementOptionalSchema>
): InternalLinkElementOptional {
    return {
        kind: 'internalLinkOptional',
        key: node.key,
        linkText: node.linkText,
        linkRoute: node.linkRoute ? sanitizeRoute(node.linkRoute) : node.linkRoute,
    };
}

function hydrateImageElementOptional(
    node: z.infer<typeof ImageElementOptionalSchema>
): ImageElementOptional {
    return {
        kind: 'imageOptional',
        key: node.key,
        imageUrl: (node.image?.fullpath && getAssetUrl(node.image.fullpath)) || null,
    };
}

function hydratePlainTextElementOptional(
    node: z.infer<typeof PlainMultilineTextElementOptionalSchema>
): PlainTextElementOptional {
    return {
        kind: 'plainTextOptional',
        key: node.key,
        content: node.content || null,
    };
}

function hydratePlainMultilineTextElementOptional(
    node: z.infer<typeof PlainMultilineTextElementOptionalSchema>
): PlainMultilineTextElementOptional {
    return {
        kind: 'plainMultilineTextOptional',
        key: node.key,
        content: node.content || null,
    };
}

function hydrateRichTextElementOptional(
    node: z.infer<typeof PlainMultilineTextElementOptionalSchema>
): RichTextElementOptional {
    const content = node.content || null;
    return {
        kind: 'richTextOptional',
        key: node.key,
        content,
        sanitizedContent: content ? sanitizeRichText(content) : null,
    };
}

/**
 * Convert a single node into a SiteContentElement, or return undefined
 * if the node type isn't one we handle.
 */
function nodeToElement(node: any): SiteContentElement | undefined {
    const key = node.key as string;
    switch (node.__typename) {
        case 'object_ExternalLinkElement':
            return {
                kind: 'externalLink',
                key,
                linkText: node.linkText,
                linkUrl: node.linkUrl,
            } satisfies ExternalLinkElement;

        case 'object_ExternalLinkElementOptional':
            return hydrateExternalLinkElementOptional(node);

        case 'object_InternalLinkElement':
            return {
                kind: 'internalLink',
                key,
                linkText: node.linkText,
                linkRoute: sanitizeRoute(node.linkRoute),
            } satisfies InternalLinkElement;

        case 'object_InternalLinkElementOptional':
            return hydrateInternalLinkElementOptional(node);

        case 'object_LinkCollectionElement':
            return {
                kind: 'linkCollection',
                key,
                links: Array.isArray(node.links)
                    ? node.links.map((e: z.infer<typeof LinkCollectionEntrySchema>) =>
                          e.__typename === 'fieldcollection_InternalLinkElement'
                              ? ({
                                    kind: 'internalLinkFieldCollection',
                                    linkText: e.linkText,
                                    linkRoute: sanitizeRoute(e.linkRoute),
                                } satisfies LinkCollectionEntryInternal)
                              : ({
                                    kind: 'externalLinkFieldCollection',
                                    linkText: e.linkText,
                                    linkUrl: e.linkUrl,
                                } satisfies LinkCollectionEntryExternal)
                      )
                    : [],
            } satisfies LinkCollectionElement;

        case 'object_InternalLinkSectionElement':
            return {
                kind: 'internalLinkSection',
                key,
                title: node.title,
                text: node.text ?? null,
                links: Array.isArray(node.links)
                    ? node.links.map((e: z.infer<typeof InternalLinkSectionEntrySchema>) =>
                          e.__typename === 'fieldcollection_InternalLinkElement'
                              ? ({
                                    kind: 'internalLinkFieldCollection',
                                    linkText: e.linkText,
                                    linkRoute: sanitizeRoute(e.linkRoute),
                                } satisfies InternalLinkSectionEntryInternal)
                              : ({
                                    kind: 'resourceLibraryItemLinkFieldCollection',
                                    linkText: e.linkText ?? null,
                                    resourceLibraryItem: {
                                        id: e.resourceLibraryItem.id,
                                        name: e.resourceLibraryItem.name,
                                    },
                                } satisfies InternalLinkSectionEntryResourceLibrary)
                      )
                    : [],
            } satisfies InternalLinkSectionElement;

        case 'object_ImageLinkCollectionElement':
            return {
                kind: 'imageLinkCollection',
                key,
                links: Array.isArray(node.links)
                    ? node.links.map(
                          (
                              e: z.infer<typeof ImageLinkCollectionEntrySchema>
                          ): ImageLinkCollectionEntry =>
                              e.__typename === 'fieldcollection_InternalImageLinkElement'
                                  ? ({
                                        kind: 'internalImageLinkFieldCollection',
                                        tooltip: e.tooltip || null,
                                        linkRoute: sanitizeRoute(e.linkRoute),
                                        imageUrl:
                                            (e.image?.fullpath && getAssetUrl(e.image.fullpath)) ||
                                            '',
                                        imageAltText:
                                            e.image?.metadata?.find((m: any) => m.name === 'alt')
                                                ?.data || null,
                                    } satisfies ImageLinkCollectionEntryInternal)
                                  : ({
                                        kind: 'externalImageLinkFieldCollection',
                                        tooltip: e.tooltip || null,
                                        linkUrl: e.linkUrl,
                                        imageUrl:
                                            (e.image?.fullpath && getAssetUrl(e.image.fullpath)) ||
                                            '',
                                        imageAltText:
                                            e.image?.metadata?.find((m: any) => m.name === 'alt')
                                                ?.data || null,
                                    } satisfies ImageLinkCollectionEntryExternal)
                      )
                    : [],
            } satisfies ImageLinkCollectionElement;

        case 'object_ImageElement':
            return {
                kind: 'image',
                key,
                imageUrl: (node.image?.fullpath && getAssetUrl(node.image.fullpath)) || '',
            } satisfies ImageElement;

        case 'object_ImageElementOptional':
            return hydrateImageElementOptional(node);

        case 'object_PlainTextElement':
            return {
                kind: 'plainText',
                key,
                content: node.content,
            } satisfies PlainTextElement;

        case 'object_PlainTextElementOptional':
            return hydratePlainTextElementOptional(node);

        case 'object_PlainMultilineTextElement':
            return {
                kind: 'plainMultilineText',
                key,
                content: node.content,
            } satisfies PlainMultilineTextElement;

        case 'object_PlainMultilineTextElementOptional':
            return hydratePlainMultilineTextElementOptional(node);

        case 'object_RichTextElement':
            return {
                kind: 'richText',
                key,
                content: node.content,
                sanitizedContent: sanitizeRichText(node.content),
            } satisfies RichTextElement;

        case 'object_RichTextElementOptional':
            return hydrateRichTextElementOptional(node);

        case 'object_TableElement':
            return {
                kind: 'table',
                key,
                tableHeader: extractTableHeader(node.tableHeader),
                tableBody: extractTableBody(node.tableBody),
            } satisfies TableElement;

        case 'object_PartitionedTableElement':
            return {
                kind: 'partitionedTable',
                key,
                commonTableHeader: extractTableHeader(node.commonTableHeader) || [],
                tablePartitions: Array.isArray(node.tablePartitions)
                    ? node.tablePartitions.map(
                          (p: z.infer<typeof PartitionedTableEntrySchema>) =>
                              ({
                                  selectionKey: p.selectionKey,
                                  tableHeader: extractTableHeader(p.tableHeader),
                                  tableBody: extractTableBody(p.tableBody),
                              }) satisfies PartitionedTableEntry
                      )
                    : [],
            } satisfies PartitionedTableElement;

        case 'object_HighlightsCollectionElement':
            return {
                kind: 'highlightsCollection',
                key,
                entries: Array.isArray(node.entries)
                    ? node.entries.map(
                          (e: z.infer<typeof HighlightsCollectionEntrySchema>) =>
                              ({
                                  title: e.title,
                                  imageUrl:
                                      (e.image?.fullpath && getAssetUrl(e.image.fullpath)) || '',
                                  text: e.text,
                              }) satisfies HighlightsCollectionEntry
                      )
                    : [],
            } satisfies HighlightsCollectionElement;

        case 'object_GovernanceRoleElement':
            return {
                kind: 'governanceRole',
                key,
                title: node.title,
                personProfiles: Array.isArray(node.personProfiles)
                    ? node.personProfiles.map(
                          (pp: z.infer<typeof GovernanceRolePersonProfileSchema>) =>
                              ({
                                  name: pp.name,
                                  content: pp.content,
                              }) satisfies GovernanceRolePersonProfile
                      )
                    : [],
            } satisfies GovernanceRoleElement;

        case 'object_ProductApplicationSegmentCardCollectionElement':
            return {
                kind: 'productApplicationSegmentCardCollection',
                key,
                entries: Array.isArray(node.entries)
                    ? node.entries.map(
                          (e: z.infer<typeof ProductApplicationSegmentCard>) =>
                              ({
                                  imageUrl:
                                      (e.image?.fullpath && getAssetUrl(e.image.fullpath)) || '',
                                  name: e.productApplicationSegment?.name || '',
                                  route: sanitizeRoute(e.route || ''),
                              }) satisfies ProductApplicationSegmentCardCollectionEntry
                      )
                    : [],
            } satisfies ProductApplicationSegmentCardCollectionElement;

        case 'object_BrandCardCollectionElement':
            return {
                kind: 'brandCardCollection',
                key,
                entries: Array.isArray(node.entries)
                    ? node.entries.map(
                          (e: z.infer<typeof BrandCard>) =>
                              ({
                                  brandId: e.brand.id,
                                  name: e.brand.name,
                                  logoUrl:
                                      (e.brand.logo?.fullpath &&
                                          getAssetUrl(e.brand.logo.fullpath)) ||
                                      '',
                                  text: e.text,
                                  externalLinkUrl: e.externalLinkUrl || null,
                              }) satisfies BrandCardCollectionEntry
                      )
                    : [],
            } satisfies BrandCardCollectionElement;

        case 'object_CompanyFocusTopicElement':
            return {
                kind: 'companyFocusTopic',
                key,
                title: node.title,
                entries: Array.isArray(node.entries)
                    ? (node.entries.map((e: z.infer<typeof CompanyFocusTopicElementEntrySchema>) =>
                          e.__typename === 'fieldcollection_ContentSectionElement'
                              ? ({
                                    kind: 'contentSectionFieldCollection',
                                    title: e.title,
                                    content: e.content,
                                    sanitizedContent: sanitizeRichText(e.content),
                                } satisfies CompanyFocusTopicContentSectionEntry)
                              : e.__typename === 'fieldcollection_ExternalLinkElement'
                                ? ({
                                      kind: 'externalLinkFieldCollection',
                                      linkText: e.linkText,
                                      linkUrl: e.linkUrl,
                                  } satisfies CompanyFocusTopicExternalLinkEntry)
                                : e.__typename === 'fieldcollection_InternalLinkElement'
                                  ? ({
                                        kind: 'internalLinkFieldCollection',
                                        linkText: e.linkText,
                                        linkRoute: sanitizeRoute(e.linkRoute),
                                    } satisfies CompanyFocusTopicInternalLinkEntry)
                                  : undefined
                      ) as CompanyFocusTopicElementEntry[])
                    : [],
            } satisfies CompanyFocusTopicElement;

        case 'object_InfoBannerCollectionElement':
            return {
                kind: 'infoBannerCollection',
                key,
                entriesPrefix: `infoBanner-${node.id}-`, // Prefix for localStorage keys
                entries: Array.isArray(node.entries)
                    ? // omit disabled entries
                      node.entries
                          .filter((e: z.infer<typeof InfoBannerCollectionEntrySchema>) => e.enabled)
                          .map(
                              (e: z.infer<typeof InfoBannerCollectionEntrySchema>) =>
                                  ({
                                      identifier: `infoBanner-${node.id}-${e.identifier}`,
                                      content: e.content,
                                      sanitizedContent: sanitizeRichText(
                                          e.content,
                                          ['a'],
                                          ['href']
                                      ),
                                  }) satisfies InfoBannerCollectionEntry
                          )
                    : [],
            } satisfies InfoBannerCollectionElement;

        case 'object_ElementReference':
            return nodeToElement(node.reference);

        default:
            return undefined;
    }
}

/**
 * Unset keys from given SiteContentElement.
 * This is useful `entries` folders that are returned as array and are intended to be managed
 * by CMS editors, so fronend should not rely on keys as unique identifiers (they can be changed).
 */
function discardKeys(obj: SiteContentElement): SiteContentElement {
    return { ...obj, key: '' } as SiteContentElement;
}

/**
 * Transforms the GraphQL `getObjectFolder` response into a nested ContentTree.
 */
export function transformToContentTree(data: unknown): ContentTree {
    const parsed = GetSiteContentElementListingSchema.safeParse(data);
    if (!parsed.success) {
        console.error(
            'Validation failed for getSiteContentElementListing (to tree):',
            parsed.error.errors
        );
        return {};
    }

    const root = (data as any)?.getObjectFolder;
    if (!root || !Array.isArray(root.children)) {
        return {};
    }

    const result: ContentTree = {};

    // Recursive traversal
    function traverse(nodes: any[], tree: ContentTree) {
        for (const node of nodes) {
            if (!node || typeof node.key !== 'string') continue;
            const key = node.key;

            // Special-case "entries" folder to handle as an array of elements
            if (key === 'entries' && Array.isArray(node.children)) {
                const arr = node.children
                    .map(nodeToElement)
                    .map(discardKeys) // Discard keys for entries as they can be modified by CMS editors
                    .filter((el: any): el is SiteContentElement => !!el);
                tree[key] = arr;
                continue;
            }

            // Folder node? (has children array)
            if (Array.isArray(node.children)) {
                const subtree: ContentTree = {};
                traverse(node.children, subtree);
                tree[key] = subtree;
                continue;
            }

            // Leaf node → single element
            const element = nodeToElement(node);
            if (element) {
                tree[key] = element;
            }
        }
    }

    traverse(root.children, result);
    return result;
}
